import { FOCUS_MAX_VISIBLE, FocusSchema } from "@uzverse/core";
import type { CameraMode, Focus } from "@uzverse/core";
import type { SceneNode } from "@uzverse/world-scene";

/** A raycast hit the user selected (or hovers): the building and, when known, the part. */
export interface WorldHit {
  id: string;
  part?: string;
  point?: readonly [number, number, number];
  normal?: readonly [number, number, number];
  distance?: number;
}

export interface CameraPose {
  position: readonly [number, number, number];
  /** Quaternion `[x, y, z, w]`. */
  rotation: readonly [number, number, number, number];
}

const round = (n: number, digits = 3): number => {
  const f = 10 ** digits;
  return Math.round(n * f) / f;
};
const vec = (v: readonly [number, number, number]): [number, number, number] => [
  round(v[0]),
  round(v[1]),
  round(v[2]),
];

/** World position of a root node (children are parent-relative; only roots are listed). */
function positionOf(node: SceneNode): [number, number, number] {
  return node.transform.position;
}

/**
 * The few prominent entities in view: pickable roots (buildings, objects) nearest the camera
 * first, at most `FOCUS_MAX_VISIBLE`.
 */
export function visibleIds(
  nodes: Iterable<SceneNode>,
  camera: readonly [number, number, number],
): string[] {
  const rows: { id: string; d: number }[] = [];
  for (const n of nodes) {
    if (!n.pickable || n.parentId !== null) continue;
    if (n.kind !== "building" && n.kind !== "object") continue;
    const p = positionOf(n);
    rows.push({ id: n.id, d: Math.hypot(p[0] - camera[0], p[1] - camera[1], p[2] - camera[2]) });
  }
  rows.sort((a, b) => a.d - b.d || (a.id < b.id ? -1 : 1));
  return rows.slice(0, FOCUS_MAX_VISIBLE).map((r) => r.id);
}

export interface FocusInput {
  selection: WorldHit | null;
  nodes: ReadonlyMap<string, SceneNode>;
  camera: CameraPose | null;
  mode?: CameraMode;
  /** The building the user is inside of (interior cutaway) and the floor they stand on. */
  inside?: { id: string; floorLevel: number } | null;
  /** The Dünya page is on screen; without it only the selection is reported. */
  active: boolean;
}

/**
 * What the user is looking at, for `chat.send` and tool calls (DESIGN §2 focus). Read at command
 * time only; gaze is never streamed. Null when there is nothing to report.
 */
export function buildFocus(input: FocusInput): Focus | null {
  const { selection, nodes, camera, active } = input;
  const target = selection && nodes.has(selection.id) ? selection : null;
  if (!target && !active) return null;
  const focus: Focus = {
    entityId: target ? target.id : null,
    ...(target ? { selection: [target.id] } : {}),
    ...(active && camera
      ? {
          camera: {
            position: vec(camera.position),
            rotation: [
              round(camera.rotation[0], 5),
              round(camera.rotation[1], 5),
              round(camera.rotation[2], 5),
              round(camera.rotation[3], 5),
            ] as [number, number, number, number],
          },
        }
      : {}),
    ...(target
      ? {
          target: {
            entityId: target.id,
            ...(target.part ? { part: target.part } : {}),
            ...(target.point ? { point: vec(target.point) } : {}),
            ...(target.normal ? { normal: vec(target.normal) } : {}),
            ...(target.distance !== undefined ? { distance: round(target.distance) } : {}),
          },
        }
      : {}),
    ...(active
      ? {
          view: {
            mode: input.mode ?? "orbit",
            ...(input.inside && nodes.has(input.inside.id)
              ? { insideId: input.inside.id, floorLevel: input.inside.floorLevel }
              : {}),
            visible: visibleIds(nodes.values(), camera?.position ?? [0, 0, 0]),
          },
        }
      : {}),
  };
  return FocusSchema.parse(focus);
}

export type PartKind = "wall" | "slab" | "roof" | "body";
export interface PartDescription {
  kind: PartKind;
  /** Floor level (0 = ground) for walls and slabs. */
  level?: number;
  side?: "n" | "e" | "s" | "w";
}

/** Reads a part id like `f2.wall.n`, `f0.slab`, `roof`, `body`. Unknown ids give null. */
export function parsePart(part: string | undefined): PartDescription | null {
  if (!part) return null;
  if (part === "roof") return { kind: "roof" };
  if (part === "body") return { kind: "body" };
  const m = /^f(\d+)\.(wall|slab)(?:\.([nesw]))?$/.exec(part);
  if (!m) return null;
  const kind = m[2] as "wall" | "slab";
  return {
    kind,
    level: Number(m[1]),
    ...(m[3] ? { side: m[3] as "n" | "e" | "s" | "w" } : {}),
  };
}
